import { del, get, set } from 'idb-keyval';

const STATIC_CACHE_NAME = 'offline-static';

const STATIC_MANIFEST_KEY = 'staticAssetsManifest';

// Caching at most this many assets in parallel avoids a fetch storm of the whole
// (~800-entry) manifest at once, which would saturate the connection and cause
// avoidable add failures on constrained links.
const ADD_CONCURRENCY = 8;

// The SW serves the SPA shell from `index.html` and that shell immediately loads
// the `main.*` entry bundle; if any of these is missing the app can't start
// offline, so we treat such a populate as failed. These are manifest keys
// (logical names), resolved to their emitted (hashed) filenames per build.
const ENTRY_ASSET_KEYS = ['index.html', 'main.js', 'main.css'];

/**
 * Features that need the static app shell cached for offline use (offline tile
 * maps, offline My Maps, …) register a provider here, so the shell is kept alive
 * — and torn down — based on the union of all offline content. Returns an
 * unregister function.
 */
const offlineContentProviders = new Set<() => Promise<boolean>>();

export function registerOfflineContentProvider(
  hasContent: () => Promise<boolean>,
): () => void {
  offlineContentProviders.add(hasContent);

  return () => {
    offlineContentProviders.delete(hasContent);
  };
}

async function hasAnyOfflineContent(): Promise<boolean> {
  for (const hasContent of offlineContentProviders) {
    if (await hasContent()) {
      return true;
    }
  }

  return false;
}

/** Resolves the load-bearing entry assets (shell + main bundle) to their URLs. */
function entryAssetUrls(manifest: Record<string, string>): string[] {
  return ENTRY_ASSET_KEYS.map((key) => manifest[key]).filter(
    (url): url is string => typeof url === 'string',
  );
}

/** True only if every URL is present in the cache. */
async function allCached(cache: Cache, urls: string[]): Promise<boolean> {
  const matches = await Promise.all(urls.map((url) => cache.match(url)));

  return matches.every(Boolean);
}

/** Adds URLs to the cache with bounded concurrency, tolerating per-URL failures. */
async function cacheUrlsTolerant(cache: Cache, urls: string[]): Promise<void> {
  let i = 0;

  await Promise.all(
    Array.from({ length: Math.min(ADD_CONCURRENCY, urls.length) }, async () => {
      while (i < urls.length) {
        await cache.add(urls[i++]).catch(() => {});
      }
    }),
  );
}

/** Caches the static app shell if it isn't cached yet (idempotent). */
export async function ensureStaticAssetsCached(): Promise<void> {
  if (!(await isStaticCacheReady())) {
    try {
      await cacheStaticAssets();
    } catch (err) {
      // Leaves the cache not-ready so a later call retries; the offline content
      // the caller just stored is unaffected.
      console.warn('Static shell caching incomplete:', err);
    }
  }
}

/** Drops the static shell once no offline content remains. */
export async function maybeClearStaticCache(): Promise<void> {
  if (!(await hasAnyOfflineContent())) {
    await clearStaticCache();
  }
}

async function fetchManifest(): Promise<Record<string, string>> {
  const res = await fetch('./assets-manifest.json');

  return res.json();
}

export async function cacheStaticAssets(): Promise<void> {
  const cache = await caches.open(STATIC_CACHE_NAME);

  const manifest = await fetchManifest();

  // Add entries individually and tolerate failures: `cache.addAll` is atomic, so
  // a single unfetchable asset (a redirect, a prerender-only HTML) would abort
  // caching the entire app shell and leave offline mode broken.
  await cacheUrlsTolerant(cache, [...new Set(Object.values(manifest))]);

  // If a load-bearing entry asset didn't cache (e.g. the network was down for
  // the whole batch), treat the populate as failed — don't write the sentinel —
  // so isStaticCacheReady stays false and ensureStaticAssetsCached retries
  // instead of leaving a half-populated cache that reports "ready" but whose
  // cached index.html references a main.* bundle that isn't there.
  if (!(await allCached(cache, entryAssetUrls(manifest)))) {
    throw new Error('static shell caching failed: entry assets not cached');
  }

  await set(STATIC_MANIFEST_KEY, manifest);
}

export async function isStaticCacheReady(): Promise<boolean> {
  // The manifest sentinel is written only after a populate completes, so it
  // distinguishes a ready cache from an empty one left behind by a failed
  // populate — which `caches.has` alone would wrongly report as ready.
  const [hasManifest, hasCache] = await Promise.all([
    get(STATIC_MANIFEST_KEY).then((m) => m !== undefined),
    caches.has(STATIC_CACHE_NAME),
  ]);

  return hasManifest && hasCache;
}

export async function clearStaticCache(): Promise<void> {
  await caches.delete(STATIC_CACHE_NAME);

  await del(STATIC_MANIFEST_KEY);
}

/**
 * Called on app startup.
 * - If no offline content exists → delete static cache.
 * - If offline content exists → diff manifest; prune stale + add new entries.
 */
export async function syncStaticCache(): Promise<void> {
  if (!(await hasAnyOfflineContent())) {
    await clearStaticCache();

    return;
  }

  if (!navigator.onLine) {
    return;
  }

  let newManifest: Record<string, string>;

  try {
    newManifest = await fetchManifest();
  } catch {
    return;
  }

  const oldManifest =
    (await get<Record<string, string>>(STATIC_MANIFEST_KEY)) ?? {};

  const newUrls = new Set(Object.values(newManifest));
  const oldUrls = new Set(Object.values(oldManifest));

  const sameSize = newUrls.size === oldUrls.size;
  const allMatch = sameSize && [...newUrls].every((u) => oldUrls.has(u));

  if (allMatch) {
    return;
  }

  const cache = await caches.open(STATIC_CACHE_NAME);

  // Add newly-fingerprinted assets first, leaving the old snapshot untouched, so
  // that a failed populate keeps the previous consistent shell serviceable.
  await cacheUrlsTolerant(
    cache,
    [...newUrls].filter((u) => !oldUrls.has(u)),
  );

  // The old entry bundle can't be pruned until the new shell + bundle are all
  // cached, otherwise the served index.html would reference a main.* hash that
  // was just deleted (or never cached) — the exact break that shows "Problem
  // starting application" offline. Bail without pruning or updating the sentinel
  // so the previous snapshot stays intact and offline startup keeps working.
  if (!(await allCached(cache, entryAssetUrls(newManifest)))) {
    console.warn('Static cache sync skipped: new entry assets not cached');

    return;
  }

  // Stable-named entries (index.html and friends map to themselves) aren't in
  // the newUrls\oldUrls diff, so their content isn't refreshed by the add above
  // even though it changed across the redeploy. Re-fetch them now that the new
  // bundle they reference is confirmed present.
  const stableUrls = Object.entries(newManifest)
    .filter(([key, value]) => key === value)
    .map(([, value]) => value);

  await cacheUrlsTolerant(cache, stableUrls);

  if (!(await allCached(cache, entryAssetUrls(newManifest)))) {
    console.warn('Static cache sync skipped: shell refresh incomplete');

    return;
  }

  // New snapshot fully cached and consistent — now safe to prune the old URLs
  // and commit the manifest sentinel.
  await Promise.all(
    [...oldUrls].filter((u) => !newUrls.has(u)).map((u) => cache.delete(u)),
  );

  await set(STATIC_MANIFEST_KEY, newManifest);
}
